/**
 * V108-8-15 (owner decision 2026-10-18) — a program's key fields, from its manifest (`programAddFields`):
 * SOC 2 report type, FedRAMP class, GovRAMP impact level + tier, ISO 42001 certification body + cycle start.
 *
 *   ProgramFieldInputs  the fields themselves (defaults preselected; text / date fields optional). The GovRAMP tier
 *                       offers only the tiers its impact level allows (`askOptionValues`).
 *   ProgramFieldsLine   on the program page: "Set up as Type II · Change". Owner / admin change it (PUT
 *                       …/program-fields; the server enforces the role). Once a period exists the form says what a
 *                       change does: a new period from the date picked, with reviewed results carried (SOC 2 type,
 *                       FedRAMP class); the GovRAMP tier opens its tier change; the impact level is fixed. Anyone else
 *                       gets Ask an admin, so it is never a dead end.
 *
 * No program list here: every label, value and rule comes from the manifest or the server.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select } from '@aigovops/design-system';
import { askOptionValues, programAddFields, type ProgramAddField, type ProgramManifest } from '@aigovops/core';
import { getProgramFields, putProgramFields, type ProgramFieldRow } from '../../lib/api';
import { invalidationMap } from '../../lib/query-keys';
import { programTitle } from '../../lib/nav-config';

type Values = Record<string, string>;

/** The values offered for a choice field, given the picks so far (GovRAMP tiers per impact level). */
export function offeredValues(m: ProgramManifest, f: Pick<ProgramAddField, 'key' | 'values'>, picked: Values): ReadonlyArray<{ value: string; label: string }> {
  return askOptionValues(m, f.key, f.values, picked);
}

/** Defaults for the add form: the manifest default, else the first value offered; optional fields start blank. */
export function defaultFieldValues(m: ProgramManifest): Values {
  const out: Values = {};
  for (const f of programAddFields(m)) {
    if (f.input !== 'choice') { out[f.key] = ''; continue; }
    const offered = offeredValues(m, f, out);
    out[f.key] = offered.some((v) => v.value === f.default) ? f.default! : (offered[0]?.value ?? '');
  }
  return out;
}

/** Keeps every choice inside what the earlier picks allow (a GovRAMP impact level change can drop the tier picked). */
export function settleFieldValues(m: ProgramManifest, values: Values): Values {
  const out: Values = { ...values };
  for (const f of programAddFields(m)) {
    if (f.input !== 'choice') continue;
    const offered = offeredValues(m, f, out);
    if (!offered.some((v) => v.value === out[f.key])) out[f.key] = offered.some((v) => v.value === f.default) ? f.default! : (offered[0]?.value ?? '');
  }
  return out;
}

/** What a field shows as text ("Type II", "BSI", "Not set"). */
export function fieldValueLabel(f: Pick<ProgramAddField, 'values' | 'input'>, value: string | null): string {
  if (!value) return 'Not set';
  if (f.input === 'choice') return (f.values.find((v) => v.value === value)?.label ?? value).split(' · ')[0]!;
  return value;
}

export function ProgramFieldInputs({ m, values, onChange, keys, idPrefix, describedBy }: {
  m: ProgramManifest;
  values: Values;
  onChange: (next: Values) => void;
  /** Only these fields (the ones that can change now); default every key field. */
  keys?: readonly string[];
  idPrefix: string;
  describedBy?: string;
}) {
  return (
    <div className="gp-form-row" data-part="program-fields">
      {programAddFields(m).filter((f) => !keys || keys.includes(f.key)).map((f) => {
        const id = `${idPrefix}-${f.key}`;
        const set = (v: string) => onChange(settleFieldValues(m, { ...values, [f.key]: v }));
        if (f.input === 'choice') {
          const offered = offeredValues(m, f, values);
          return (
            // Flow B (review F-1): a field that is fixed once assessed says so where it is chosen.
            <Field key={f.key} id={id} label={f.label} hint={f.lockedOnceAssessed ? 'Fixed once the first period starts' : f.hint ?? undefined} describedBy={describedBy}>
              <Select value={values[f.key] ?? ''} onChange={(e) => set(e.target.value)} data-part={`field-${f.key}`}>
                {offered.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </Select>
            </Field>
          );
        }
        return (
          <Field key={f.key} id={id} label={<>{f.label} <span className="gp-field-optional">Optional</span></>}>
            <Input type={f.input === 'date' ? 'date' : 'text'} value={values[f.key] ?? ''} maxLength={200} onChange={(e) => set(e.target.value)} data-part={`field-${f.key}`} />
          </Field>
        );
      })}
    </div>
  );
}

const fmtDay = (d: string) => { const t = Date.parse(`${d.slice(0, 10)}T00:00:00Z`); return Number.isFinite(t) ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : d; };
const dayBefore = (d: string) => { const t = Date.parse(`${d}T00:00:00Z`); return Number.isFinite(t) ? fmtDay(new Date(t - 86_400_000).toISOString()) : null; };

/**
 * The program's key fields under its status: the option values the title doesn't say, and Change for an owner / admin
 * (only the fields that can change now: GovRAMP's tier has its own tier ladder, a locked impact level is not offered).
 */
export function ProgramFieldsLine({ slug, profileId, m, isAdmin }: { slug: string; profileId: string; m: ProgramManifest; isAdmin: boolean; prefix?: string }) {
  const uid = useId();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['program-fields', slug, profileId], queryFn: () => getProgramFields(slug, profileId), retry: false, staleTime: 30_000, enabled: programAddFields(m).length > 0 });
  // Flow M (review): "today" is the person's local date, not UTC (an evening change in the US is still today).
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Values>({});
  const [from, setFrom] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  // Security review M-1: ending a period's fieldwork is confirmed explicitly (the server refuses it otherwise).
  const [endFieldwork, setEndFieldwork] = useState(false);
  const changeRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // A11y H (review): the form opens with focus on its first field; it never drops focus on <body>.
  useEffect(() => { if (open) formRef.current?.querySelector<HTMLElement>('select, input')?.focus(); }, [open]);
  const rows = q.data?.fields ?? [];
  const editable = rows.filter((r) => r.on_change === 'update' || r.on_change === 'new_period');
  const changed = editable.filter((r) => (values[r.key] ?? '') !== (r.value ?? ''));
  const newPeriod = open && changed.some((r) => r.on_change === 'new_period');
  const body = { fields: Object.fromEntries(changed.map((r) => [r.key, values[r.key] ? values[r.key]! : null])), ...(newPeriod ? { effective_from: from } : {}) };
  // Flow H (review F-3): before saving, say which period closes, when, and how many reviewed results carry (a dry run;
  // it previews as if the fieldwork end were confirmed, so the consequence shows before the person confirms it).
  const preview = useQuery({ queryKey: ['program-fields-preview', slug, profileId, JSON.stringify(body)], queryFn: () => putProgramFields(slug, profileId, { ...body, close_fieldwork: true, dry_run: true }),
    enabled: newPeriod && /^\d{4}-\d{2}-\d{2}$/.test(from), retry: false, staleTime: 10_000 });
  if (!programAddFields(m).length || q.isLoading) return null;
  if (q.error || !q.data) {
    return <p className="gp-fields-line" data-part="program-fields-line">{`${programTitle(m)} settings couldn't load. `}<button type="button" className="gp-inline-btn" onClick={() => void q.refetch()}>Retry</button></p>;
  }
  // The title and the header line already say the profile columns (SOC 2 type, FedRAMP class): show the options only,
  // and never a field another part of the page owns (GovRAMP's tier: its tier ladder; review A-9 / G-7).
  const shown = rows.filter((r) => r.target === 'option' && r.value && r.on_change !== 'tier_change');
  const canChange = isAdmin && editable.length > 0;
  // Nit (round 2): once nothing can change here and the page says it elsewhere (GovRAMP's tier row), say nothing.
  if ((!shown.length || (!canChange && q.data.has_period && rows.some((r) => r.on_change === 'tier_change'))) && !canChange && !status) return null;
  const what = editable.length === 1 ? editable[0]!.label.toLowerCase() : 'settings';
  const start = () => { setValues(Object.fromEntries(rows.map((r) => [r.key, r.value ?? '']))); setFrom(today); setEndFieldwork(false); setError(null); setStatus(''); setOpen(true); };
  // Review M-1: no earlier than 7 days ago (the server's EFFECTIVE_FROM_GRACE_DAYS; it also refuses a start before the
  // period's freeze or latest reviewed result, and says so in the preview).
  const earliest = (() => { const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const close = () => { setOpen(false); window.setTimeout(() => changeRef.current?.focus(), 0); };
  const np = preview.data?.change.new_period ?? null;
  const prev = np?.previous_period ?? null;
  const pc = np?.previous_change ?? null;
  const carry = np?.carry ?? null;
  const left = carry ? carry.left_assessor + carry.left_type1_design : 0;
  const fieldwork = newPeriod && prev?.status === 'fieldwork';
  // A11y M (round 3): a start before the bound is said in place (never only a browser tooltip).
  const tooEarly = newPeriod && /^\d{4}-\d{2}-\d{2}$/.test(from) && from < earliest ? `Pick ${fmtDay(earliest)} or later: a change can take effect at most 7 days back.` : null;
  const previewError = tooEarly ?? (newPeriod && preview.error instanceof Error ? preview.error.message : null);
  // Flow M (round 3): while the preview loads, say so and keep Save inert — the person only confirms the server's answer.
  const checking = newPeriod && !previewError && (preview.isFetching || !preview.data);
  // Glance / aesthetic M (round 2): one consequence line; the period is named by what it covers. Review M-3: what
  // carries and what stays behind, in plain words.
  const effect = previewError ?? (checking ? 'Checking what this changes…' : newPeriod
    ? [pc ? `Ends the period covering ${fmtDay(pc.window_before[0])} – ${fmtDay(pc.window_before[1])}${pc.window_after[1] !== pc.window_before[1] ? ` on ${fmtDay(pc.window_after[1])}` : ''}`
        : prev ? `Starts after the closed period ${fmtDay(prev.period_start)} – ${fmtDay(prev.period_end)}` : `The current period ends ${dayBefore(from) ?? 'the day before'}`,
      carry ? `${carry.carried} reviewed ${carry.carried === 1 ? 'result carries' : 'results carry'} over` : null,
      left ? `${left} ${[carry!.left_assessor ? 'assessor' : null, carry!.left_type1_design ? 'Type I design' : null].filter(Boolean).join(' / ')} ${left === 1 ? 'result stays' : 'results stay'} in the closed period and need assessing again` : null,
    ].filter(Boolean).join(' · ')
    : null);
  const save = async () => {
    if (busy || !changed.length || previewError || checking) return;
    if (fieldwork && !endFieldwork) { setError('Tick “End this period’s fieldwork” to save this change.'); return; }
    setBusy(true); setError(null);
    try {
      const { change } = await putProgramFields(slug, profileId, { ...body, ...(fieldwork && endFieldwork ? { close_fieldwork: true } : {}) });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      setStatus(change.new_period
        ? `Saved. A new period starts ${fmtDay(change.new_period.period.period_start)}; ${change.new_period.carried} reviewed ${change.new_period.carried === 1 ? 'result' : 'results'} carried over.`
        : 'Saved.');
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : `${programTitle(m)} settings could not be saved. Try again.`);
    } finally { setBusy(false); }
  };
  const formId = `${uid}-form`;
  return (
    <section className="gp-fields-line" aria-label={`${programTitle(m)} settings`} data-part="program-fields-line">
      <p className="gp-fields-summary">
        {shown.map((r, i) => <span key={r.key}>{i ? ' · ' : ''}{`${r.label} `}<b>{fieldValueLabel(r, r.value)}</b></span>)}
        {canChange ? <button ref={changeRef} type="button" className="gp-inline-btn" onClick={() => (open ? close() : start())} aria-expanded={open} aria-controls={open ? formId : undefined}>
          {`Change ${what}`}<span className="gp-fields-sr">{`: ${programTitle(m)}`}</span></button> : null}
      </p>
      {/* One status region, always mounted (only its text changes). */}
      <p role="status" className="gp-fields-done" data-part="program-fields-status">{status}</p>
      {open ? (
        <form id={formId} ref={formRef} className="gp-form gp-fields-form" onSubmit={(e) => { e.preventDefault(); void save(); }} aria-labelledby={`${uid}-ft`}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } }}>
          <p id={`${uid}-ft`} className="gp-fields-title">{`Change ${programTitle(m)} ${what}`}</p>
          <ProgramFieldInputs m={m} values={values} onChange={setValues} keys={editable.map((r) => r.key)} idPrefix={uid} describedBy={`${uid}-effect`} />
          {changed.length ? <p className="gp-fields-delta" data-part="change-summary">{changed.map((r) => `${changed.length > 1 ? `${r.label}: ` : ''}${fieldValueLabel(r, r.value)} → ${fieldValueLabel(r, values[r.key] || null)}`).join(' · ')}</p> : null}
          {newPeriod ? (
            <div className="gp-fields-period" data-part="new-period-note">
              <Field id={`${uid}-from`} label="New period starts">
                <Input type="date" value={from} min={earliest} onChange={(e) => setFrom(e.target.value)} required />
              </Field>
            </div>
          ) : null}
          {/* A11y M (review): the consequence of a change is announced when it appears. */}
          <p id={`${uid}-effect`} className="gp-fields-effect" data-tone={previewError || fieldwork ? 'warn' : undefined} aria-live="polite" data-part="change-effect">{effect ?? (changed.length ? '' : 'Change a setting to save.')}</p>
          {/* Aesthetic M (round 3): read what ends first, then confirm it. */}
          {fieldwork && !previewError ? (
            <label className="gp-fields-confirm" data-part="end-fieldwork">
              <input type="checkbox" checked={endFieldwork} onChange={(e) => { setEndFieldwork(e.target.checked); if (e.target.checked) setError(null); }}
                aria-invalid={error && !endFieldwork ? true : undefined} aria-describedby={[`${uid}-effect`, error ? `${uid}-err` : null].filter(Boolean).join(' ')} />
              <span>{pc ? `End this period’s fieldwork on ${fmtDay(pc.window_after[1])}` : 'End this period’s fieldwork'}</span>
            </label>
          ) : null}
          {error ? <div id={`${uid}-err`}><Alert variant="error">{error}</Alert></div> : null}
          <div className="gp-actions">
            {/* Flow H (review F-7): one filled primary per surface — the page's next step keeps it; this commit is outlined. */}
            <Button type="submit" variant="secondary" aria-disabled={busy || !changed.length || !!previewError || checking || (fieldwork && !endFieldwork) || undefined} aria-describedby={`${uid}-effect`}>{busy ? 'Saving…' : newPeriod ? 'Save and start a new period' : 'Save'}</Button>
            <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

export type { ProgramFieldRow };
